<template>
    <redesign-base-chart
        ref="chartRef"
        class="pt-4"
        :widget="data"
        :option="chartOptions"
    />
</template>

<script setup lang="ts">
import { WidgetExtended } from '@/types/dashboard'
import { computed, ref } from 'vue'
import RedesignBaseChart from '@/components/RedesignComponents/Charts/RedesignBaseChart.vue'
import useWidgetDataWithRefreshInterval from '@/composables/useWidgetDataWithRefreshInterval'
import store from '@/store/store'
import widgetDataTypes from '@/enum/widgetDataTypes'
import { DateTimeChartData, BarsLinesData } from '@/types/timeLineCharts'
import { ChartOptions } from '@/types/echart'
import { ComponentExposed } from 'vue-component-type-helpers'

const props = defineProps<{
    data: WidgetExtended
}>()

const chartRef = ref<ComponentExposed<typeof RedesignBaseChart>>()

const { data: loadedData } = useWidgetDataWithRefreshInterval<DateTimeChartData[] | BarsLinesData[], {data: WidgetExtended}, 'data'>(props, 'data')

const serverDelta = computed(() => {
    return store.state.extensions.serverDelta
})

const serverOffSet = computed(() => {
    return store.state.extensions.serverOffset
})

const displayLegend = computed(() => {
    return props.data?.WidgetLayout?.displayLegend ?? true
})

const chartOptions = computed(() => transformDataToECharts(loadedData.value))

function chunkArray <T> (arr: T[], chunkSize: number): T[][] {
    const chunks: T[][] = []
    for (let i = 0; i < arr.length; i += chunkSize) {
        chunks.push(arr.slice(i, i + chunkSize))
    }
    return chunks
}

function buildLegends (legendData: string[], widgetWidth: number, maxColumns = 5) {
    const approxLegendItemWidth = 120 // Approximate width per legend item (icon + text + gap)
    const gridLeftPadding = 60
    const gridRightPadding = 40
    const availableWidth = widgetWidth - gridLeftPadding - gridRightPadding

    // Calculate if all items fit horizontally in one row
    const totalHorizontalWidth = legendData.length * approxLegendItemWidth
    const fitsHorizontally = totalHorizontalWidth <= availableWidth

    // If items fit in one horizontal row, use centered horizontal layout
    if (fitsHorizontally) {
        return {
            legend: {
                orient: 'horizontal',
                left: 'center',
                bottom: 5,
                itemGap: 12,
                itemWidth: 25,
                itemHeight: 14,
                textStyle: {
                    fontSize: 11,
                },
                data: legendData
            },
            dynamicBottom: 60
        }
    }

    // Otherwise, use vertical multi-column layout
    let columns = Math.floor(availableWidth / 150)
    columns = Math.max(1, Math.min(columns, maxColumns))

    const chunked = chunkArray(legendData, Math.ceil(legendData.length / columns))
    const maxRows = Math.max(...chunked.map(c => c.length))
    const rowHeight = 22
    const dynamicBottom = 40 + (rowHeight * maxRows)

    const columnWidth = 150
    const totalColumnsWidth = columnWidth * columns
    const startOffset = (widgetWidth - totalColumnsWidth) / 2

    const legends = chunked.map((chunk, i) => ({
        orient: 'vertical',
        left: startOffset + (columnWidth * i),
        bottom: 5,
        itemGap: 8,
        itemWidth: 25,
        itemHeight: 14,
        textStyle: {
            fontSize: 11,
        },
        data: chunk
    }))

    return {
        legend: legends,
        dynamicBottom
    }
}

function isBarsData (data: unknown): data is BarsLinesData {
    return (
        props.data.DataTypeID === widgetDataTypes.BARS_WITH_LINES_TYPE_ID
        && typeof data === 'object'
        && data !== null
        && data !== undefined
        && 'xAxis' in data
    )
}

function isLinesData (data: unknown): data is DateTimeChartData {
    return (
        props.data.DataTypeID === widgetDataTypes.LINES_TYPE_ID
        && typeof data === 'object'
        && data !== null
        && data !== undefined
    )
}

function transformDataToECharts(data: BarsLinesData[]): ChartOptions
function transformDataToECharts(data: DateTimeChartData[]): ChartOptions
function transformDataToECharts (data: BarsLinesData[] | DateTimeChartData[]) {
    if (!data || !data.length) return {}

    const chartData = data[0]

    const isTimeChart = (chartData as any)?.xAxis?.type === 'datetime'
    let categories: string[] | null = null
    let series
    let yAxis

    if (isBarsData(chartData)) {
        series = chartData?.series?.map(serie => ({
            name: serie.name,
            type: serie.type === 'spline' ? 'line' : 'bar',
            data: serie.data,
            yAxisIndex: serie.yAxis,
            itemStyle: {
                color: serie.color
            },
            smooth: serie.type === 'spline'
        }))

        yAxis = chartData.yAxis?.map(y => ({
            type: 'value',
            name: y?.title?.text || '',
            position: y?.opposite === 'true' ? 'right' : 'left',
            nameLocation: 'middle',
            nameGap: 45,
            nameRotate: y?.opposite === 'true' ? -90 : 90,
            axisLabel: { color: y?.title?.style?.color },
            nameTextStyle: { color: y?.title?.style?.color }
        }))

        categories = chartData?.xAxis?.[0]?.categories ?? null

    } else if (isLinesData(chartData)) {
        series = chartData?.series?.map(serie => ({
            name: serie.name,
            type: 'line',
            emphasis: {
                focus: 'series',
            },
            smooth: false,
            data: serie.data.map(point => [
                point.x * 1000 - serverOffSet.value + serverDelta.value,
                point.y
            ])
        }))

        yAxis = [ {
            type: 'value',
            name: chartData.YxisName || '',
            position: 'left',
            nameLocation: 'middle',
            nameGap: 45,
            nameRotate: 90,
        } ]
    }

    const seriesCount = chartData?.series?.length || 0
    const hasRotatedLabels = categories && (categories?.length || 0) > 10

    // Get widget width for dynamic column calculation
    const widgetWidth = chartRef.value?.getChartWidth() || 600
    const legendNames = chartData?.series?.map(s => s.name) || []

    // Build dynamic multi-column legends at bottom
    const { legend, dynamicBottom } = displayLegend.value && seriesCount > 0
        ? buildLegends(legendNames, widgetWidth)
        : {
            legend: { show: false },
            dynamicBottom: 40
        }

    // Calculate bottom spacing (legend + rotated labels if needed)
    let bottomSpacing = dynamicBottom
    if (hasRotatedLabels) {
        bottomSpacing += 40 // Extra space for rotated labels
    }

    return {
        tooltip: {
            trigger: 'axis',
            axisPointer: {
                type: 'line',
                snap: true, // aligns to closest point
                label: {
                    show: true,
                }
            }
        },
        emphasis: {
            focus: 'series'
        },
        legend: legend,
        xAxis: isTimeChart
            ? {
                type: 'time',
                axisLabel: {
                    formatter: function (value) {
                        const date = new Date(value)

                        const hours = date.getHours()
                        const minutes = date.getMinutes()

                        const day = date.getDate()
                        const month = date.toLocaleString('en-US', { month: 'long' })

                        const isMidnight = hours === 0 && minutes === 0

                        if (isMidnight) {
                            return `${day} ${month}`
                        }

                        const hh = String(hours).padStart(2, '0')
                        const mm = String(minutes).padStart(2, '0')

                        return `${hh}:${mm}`
                    }
                }
            }
            : {
                type: 'category',
                data: categories || [],
                axisLabel: {
                    interval: 0,
                    rotate: (categories?.length || 0) > 10 ? 45 : 0
                }
            },
        yAxis,
        series,
        grid: {
            top: 40,
            bottom: bottomSpacing,
            left: 60,
            right: 40,
            containLabel: true
        }
    }
}
</script>

<style scoped lang="scss">

</style>
